/**
 * CR043: READ Tab Component for POC Run Detail
 */

import { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Eye, ExternalLink } from "lucide-react";
import { safeHostname } from "@/lib/safeString";
import { ReadStatusBadge } from "./ReadStatusBadge";
import { ReadQualityFlagChips } from "./ReadQualityFlagChips";
import { ReadAttemptsDrawer } from "./ReadAttemptsDrawer";
import type { PocRunMerchantRead } from "@/lib/read-phase/types";

interface ReadTabContentProps {
  merchants: PocRunMerchantRead[];
}

export function ReadTabContent({ merchants }: ReadTabContentProps) {
  const [selectedMerchant, setSelectedMerchant] = useState<PocRunMerchantRead | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleViewAttempts = (merchant: PocRunMerchantRead) => {
    setSelectedMerchant(merchant);
    setDrawerOpen(true);
  };

  if (merchants.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No READ data recorded
      </div>
    );
  }

  return (
    <>
      <ScrollArea className="h-[400px]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Status</TableHead>
              <TableHead>Merchant</TableHead>
              <TableHead>Source URL</TableHead>
              <TableHead className="text-center">Attempts</TableHead>
              <TableHead className="text-center">Quality</TableHead>
              <TableHead>Flags</TableHead>
              <TableHead className="text-center">Text Length</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {merchants.map((merchant) => (
              <TableRow key={merchant.id}>
                <TableCell>
                  <ReadStatusBadge status={merchant.read_status} />
                </TableCell>
                <TableCell className="font-medium">
                  {merchant.merchant_name || merchant.merchant_id}
                </TableCell>
                <TableCell className="max-w-[200px]">
                  {(() => {
                    const enriched = merchant as any;
                    const effectiveUrl = enriched._effective_url;
                    const scanType = enriched._scan_type;
                    const sourceInvalid = enriched._source_invalid;

                    if (sourceInvalid) {
                      return (
                        <Badge variant="destructive" className="text-xs">
                          Invalid source type ({scanType || 'unknown'})
                        </Badge>
                      );
                    }
                    if (effectiveUrl) {
                      return (
                        <a
                          href={effectiveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1 truncate"
                        >
                          {safeHostname(effectiveUrl)}
                          <ExternalLink className="h-3 w-3 flex-shrink-0" />
                        </a>
                      );
                    }
                    if (merchant.source_url) {
                      return (
                        <span className="text-xs text-muted-foreground flex items-center gap-1 truncate" title="Fallback: raw source_url (not resolved via run snapshot)">
                          {safeHostname(merchant.source_url)}
                        </span>
                      );
                    }
                    return (
                      <span className="text-muted-foreground text-xs">Missing WEB source URL</span>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline" className="font-mono">
                    {merchant.read_attempts_count || 0}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  {merchant.read_quality_score != null ? (
                    <Badge 
                      variant={merchant.read_quality_score >= 0.7 ? 'default' : merchant.read_quality_score >= 0.4 ? 'secondary' : 'destructive'}
                      className="font-mono"
                    >
                      {merchant.read_quality_score.toFixed(2)}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground text-xs">-</span>
                  )}
                </TableCell>
                <TableCell className="max-w-[200px]">
                  <ReadQualityFlagChips flags={merchant.read_quality_flags} maxVisible={2} />
                </TableCell>
                <TableCell className="text-center font-mono text-xs">
                  {merchant.text_char_count?.toLocaleString() || '-'}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleViewAttempts(merchant)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View Attempts
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ScrollArea>

      <ReadAttemptsDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        merchant={selectedMerchant}
      />
    </>
  );
}
